import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { createCaseTask, listCaseTasks, transitionTaskStatus, updateTask } from '../../api/tasks'
import { ApiError } from '../../api/problem'
import { TASK_PRIORITY_LABEL, TASK_STATUS_LABEL, type TaskStatus, type TaskView } from '../../types/task'
import { TaskFormModal, type TaskFormValues } from '../tasks/TaskFormModal'

export interface TasksPanelProps {
  caseId: string
}

const STATUS_VARIANT: Record<TaskStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
  todo: 'neutral',
  in_progress: 'info',
  blocked: 'warning',
  done: 'success',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
}

/**
 * The Case-workspace "Tasks" tab — every `Task` linked to this Case
 * (`GET /cases/{caseId}/tasks`), with create/edit via the shared
 * `TaskFormModal`. Single-case, so no Account/Legal-entity/Fiscal-year
 * columns here (CLAUDE.md §4's cross-case-list rule does not apply — every
 * row is already this one Case).
 */
export function TasksPanel({ caseId }: TasksPanelProps) {
  const [rows, setRows] = useState<TaskView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'create' | TaskView | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listCaseTasks(caseId)
      .then((page) => {
        setRows(page.items)
        setState('ready')
      })
      .catch((err) => {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load tasks.')
        setState('error')
      })
  }, [caseId])

  useEffect(() => {
    load()
  }, [load])

  async function handleSave(values: TaskFormValues) {
    setActionError(null)
    try {
      if (modal === 'create') {
        const created = await createCaseTask(caseId, values)
        setRows((prev) => [created, ...prev])
      } else if (modal) {
        const updated = await updateTask(modal.id, values)
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      }
      setModal(null)
    } catch (err) {
      setActionError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not save this task.')
      throw err
    }
  }

  async function handleTransition(taskId: string, toStatus: TaskView['status']) {
    setActionError(null)
    try {
      const updated = await transitionTaskStatus(taskId, toStatus)
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      setModal((current) => (current && current !== 'create' && current.id === updated.id ? updated : current))
    } catch (err) {
      setActionError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not change status.')
    }
  }

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading tasks…</p>
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="iw-btn iw-btn--primary" onClick={() => setModal('create')}>
          <Icon name="plus" size={14} /> New task
        </button>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No tasks yet for this case.</p>
      ) : (
        <table className="iw-table" aria-label="Case tasks">
          <thead>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Status</th>
              <th scope="col">Priority</th>
              <th scope="col">Due</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>
                  <StatusPill variant={STATUS_VARIANT[row.status]} label={TASK_STATUS_LABEL[row.status]} />
                </td>
                <td>{TASK_PRIORITY_LABEL[row.priority]}</td>
                <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                  {formatDate(row.dueDate)}
                </td>
                <td>
                  <button type="button" className="iw-btn iw-btn--sm" onClick={() => setModal(row)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal ? (
        <TaskFormModal
          task={modal === 'create' ? undefined : modal}
          fixedCaseId={caseId}
          onClose={() => setModal(null)}
          onSave={handleSave}
          onTransitionStatus={
            modal === 'create' ? undefined : (toStatus) => handleTransition((modal as TaskView).id, toStatus)
          }
        />
      ) : null}
    </div>
  )
}
